import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { formatCurrencyLocalized } from '../../../../utils/currency';
import { formatLocalizedPercent } from '../../../../utils/numberFormatting';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../../utils/polishNumberInput';
import { formatCommercialPriceUnit } from '../../commercialPriceUnit';
import { getOrderProductDisplayDimensions, getOrderProductDisplayName } from '../../orderProductDisplay';
import type { OrderFifoPreviewLine } from '../../api/orderProductsApi';
import { useOrderMarginPreview } from '../../queries/useOrderMarginPreview';
import type { OrderDraftLine } from '../draft/orderDraftModel';
import { unitPriceInputProps } from '../../../../utils/money';
import type { OrderMarginVisibility } from '../orderMarginStage';
import { HiddenValue } from '../OrderProcessParts';
import { useQuantityFormatter } from '../OrderProcessTables';
import { arePricesComplete, getOrderPricesPreviewRequest, parseOrderPriceInput } from './orderPricesRules';
import './OrderGuide.css';

export interface OrderPricesDialogProps {
  open: boolean;
  orderId: string;
  orderNumber: string;
  /** Lines of the editor (saved ones carry an id). */
  lines: OrderDraftLine[];
  /** Lines as saved: the server prices these. */
  savedLines: OrderDraftLine[];
  /** Cost of the lines as the card already knows it (planned-margin preview of the saved lines). */
  knownLines: OrderFifoPreviewLine[];
  marginVisibility: OrderMarginVisibility;
  orderCurrency: string;
  saving: boolean;
  onCancel: () => void;
  /** Prices by line key; the page puts them into the draft and saves it. */
  onSave: (prices: Record<string, number>) => void;
}

const COST_BASIS_KEYS = {
  WAREHOUSE: 'orders:process.pricesDialog.basis.WAREHOUSE',
  MIXED: 'orders:process.pricesDialog.basis.MIXED',
  AVERAGE_ESTIMATE: 'orders:process.pricesDialog.basis.AVERAGE_ESTIMATE',
  UNKNOWN: 'orders:process.pricesDialog.basis.UNKNOWN',
} as const;

/**
 * „Ceny sprzedaży — <nr>” (first mockup, `pricesModal`): the price of every
 * line with the margin beside it and the tiles Przychód / Koszt łącznie /
 * Planowana marża, live while a price is typed. The margin is asked of the
 * server (`margin-preview`, saved lines at the typed prices) — the window
 * computes nothing. Handlowiec: revenue, margin and percent, costs read
 * „koszt widzi administrator”. „Zapisz ceny” puts the prices into the draft and
 * saves it (the same save as „Zapisz szkic”).
 */
export default function OrderPricesDialog({
  open,
  orderId,
  orderNumber,
  lines,
  savedLines,
  knownLines,
  marginVisibility,
  orderCurrency,
  saving,
  onCancel,
  onSave,
}: OrderPricesDialogProps) {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const quantity = useQuantityFormatter();
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  // The typed prices start from the draft each time the window opens.
  useEffect(() => {
    if (open) setPrices(Object.fromEntries(lines.map((line) => [line.key, Number(line.unitPrice) > 0 ? Number(line.unitPrice) : null])));
    // Only on open: typing must not be reset by a refreshed draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const complete = arePricesComplete(prices, lines);
  const request = useMemo(() => getOrderPricesPreviewRequest(prices, lines, savedLines), [prices, lines, savedLines]);
  const live = useOrderMarginPreview(orderId, request.prices, open && marginVisibility.showMargin);
  const money = (value: number | null | undefined) => (value == null ? '—' : formatCurrencyLocalized(value, 'PLN', i18n.language));
  const percent = (value: number | null | undefined) => (value == null ? '—' : formatLocalizedPercent(value, i18n.language));
  const lineInfo = (line: OrderDraftLine): OrderFifoPreviewLine | undefined => (
    live.preview?.lines.find((row) => row.id === line.id) ?? knownLines.find((row) => row.id === line.id)
  );
  const showCosts = marginVisibility.showCosts;

  const costLabel = (line: OrderDraftLine) => {
    const info = lineInfo(line);
    if (!marginVisibility.showMargin || !info?.costBasis) return null;
    const basis = t(COST_BASIS_KEYS[info.costBasis]);
    if (!showCosts) return t('orders:process.pricesDialog.costHidden', { basis });
    if (info.purchaseUnitPrice == null) return t('orders:process.pricesDialog.costUnknown');
    return t('orders:process.pricesDialog.cost', {
      cost: money(info.purchaseUnitPrice),
      unit: formatCommercialPriceUnit('sale', line, i18n.language),
      basis,
    });
  };

  const marginCell = (line: OrderDraftLine) => {
    if (!marginVisibility.showMargin) return null;
    if (!parseOrderPriceInput(prices[line.key])) {
      return prices[line.key] != null ? <span className="zk-prices__error">{t('orders:process.pricesDialog.invalid')}</span> : '—';
    }
    const row = live.preview?.lines.find((item) => item.id === line.id);
    if (!row) return <span className="zk-prices__muted">{live.loading ? '…' : '—'}</span>;
    if (row.costBasis === 'UNKNOWN' || (showCosts ? row.marginPLN == null : row.marginPercent == null)) {
      return <span className="zk-prices__muted">{t('orders:process.margin.unknown')}</span>;
    }
    const negative = showCosts ? Number(row.marginPLN) < 0 : Number(row.marginPercent) < 0;
    return (
      <span className={`zk-num${negative ? ' zk-mcard__neg' : ''}`} data-testid="zk-prices-line-margin">
        {showCosts && <b>{money(row.marginPLN)}<br /></b>}
        <span className="zk-mcard__sub">{percent(row.marginPercent)}</span>
      </span>
    );
  };

  const preview = live.preview;
  const marginTile = (() => {
    if (!complete) return t('orders:process.pricesDialog.fillPrices');
    if (request.block === 'unsaved') return t('orders:process.pricesDialog.afterSave');
    if (!preview) return live.failed ? t('orders:process.pricesDialog.previewFailed') : '…';
    if (preview.costPending || preview.marginPLN == null) return t('orders:process.margin.unknown');
    return `${money(preview.marginPLN)} · ${percent(preview.marginPercent)}`;
  })();
  const loss = Boolean(preview && !preview.costPending && preview.marginPLN != null && preview.marginPLN < 0);

  return (
    <Modal
      open={open}
      title={t('orders:process.pricesDialog.title', { orderNumber })}
      okText={t('orders:process.pricesDialog.save')}
      cancelText={t('common:cancel')}
      okButtonProps={{ disabled: !complete, ...({ 'data-testid': 'zk-prices-save' } as Record<string, string>) }}
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={() => {
        if (!complete) return;
        onSave(Object.fromEntries(lines.map((line) => [line.key, parseOrderPriceInput(prices[line.key]) as number])));
      }}
      destroyOnHidden
      width={680}
      styles={{ body: { maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' } }}
    >
      <div className="zk-prices" data-testid="zk-prices-dialog">
        <div className="zk-prices__head" aria-hidden="true">
          <span>{t('orders:process.lines.product')}</span>
          <span className="zk-prices__r">{t('orders:process.lines.quantity')}</span>
          <span>{t('orders:process.pricesDialog.price')}</span>
          {marginVisibility.showMargin && <span className="zk-prices__r">{t('orders:process.marginCard.columns.margin')}</span>}
        </div>
        <ul className="zk-prices__rows">
          {lines.map((line) => {
            const name = getOrderProductDisplayName(line as never, line.name || '');
            const dimensions = getOrderProductDisplayDimensions(line);
            const currency = line.currency || orderCurrency;
            return (
              <li className="zk-prices__row" key={line.key} data-testid="zk-prices-row">
                <div className="zk-prices__name">
                  <b>{name}</b>
                  {dimensions && <div className="zk-prices__muted" data-testid="zk-prices-line-dimensions">{dimensions}</div>}
                  {costLabel(line) && <div className="zk-prices__muted">{costLabel(line)}</div>}
                </div>
                <div className="zk-prices__qty zk-num"><span className="zk-prices__label">{t('orders:process.lines.quantity')}: </span>{quantity(Number(line.quantity), line.unit)}</div>
                <div className="zk-prices__price">
                  <InputNumber
                    max={1000000}
                    {...unitPriceInputProps(line.unitPrice)}
                    {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    status={prices[line.key] != null && !parseOrderPriceInput(prices[line.key]) ? 'error' : undefined}
                    value={prices[line.key] ?? null}
                    controls={false}
                    placeholder="0,00"
                    addonAfter={`${currency} / ${formatCommercialPriceUnit('sale', line, i18n.language)}`}
                    aria-label={t('orders:process.pricesDialog.priceAria', { name })}
                    onChange={(value) => setPrices((current) => ({ ...current, [line.key]: value == null ? null : Number(value) }))}
                    onPressEnter={() => { if (complete && !saving) onSave(Object.fromEntries(lines.map((row) => [row.key, parseOrderPriceInput(prices[row.key]) as number]))); }}
                    data-testid="zk-prices-input"
                  />
                </div>
                {marginVisibility.showMargin && <div className="zk-prices__margin">{marginCell(line)}</div>}
              </li>
            );
          })}
        </ul>
        {marginVisibility.showMargin && (
          <div className="zk-msum" data-testid="zk-prices-tiles">
            <div>
              <span>{t('orders:process.marginCard.revenue')}</span>
              <b className="zk-num">{preview ? money(preview.revenuePLN) : '—'}</b>
            </div>
            <div>
              <span>{t('orders:process.marginCard.totalCost')}</span>
              <b className="zk-num">
                {showCosts ? (preview?.knownCostPLN != null && !preview.costPending ? money(preview.knownCostPLN) : '—') : <HiddenValue reason="costs" />}
              </b>
            </div>
            <div className={`zk-msum__big${loss ? ' zk-msum__big--loss' : ''}`}>
              <span>{t('orders:process.margin.stage.PLANNED')}</span>
              <b className="zk-num" data-testid="zk-prices-margin">{marginTile}</b>
            </div>
          </div>
        )}
        <p className="zk-section__hint zk-prices__help">
          {t(marginVisibility.showMargin ? 'orders:process.pricesDialog.help' : 'orders:process.pricesDialog.helpNoMargin')}
        </p>
      </div>
    </Modal>
  );
}
